import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Target, TrendingUp, PenTool, Check, ArrowRight, Shield, Star, Zap } from "lucide-react";

const FEATURES = [
  {
    icon: Target,
    title: "Application Analyzer",
    desc: "Analyze your CV or Supporting Information against the Job Description and Person Specification. Get match scores, PS coverage, shortlisting simulation, and improvement advice.",
    color: "#003087",
  },
  {
    icon: FileText,
    title: "NHS Supporting Info Generator",
    desc: "Generate structured supporting statements mapped to essential and desirable criteria.",
    color: "#00A3A6",
  },
  {
    icon: TrendingUp,
    title: "Band Progression Coach",
    desc: "AI-powered career mentoring with gap analysis, roadmaps, and portfolio checklists.",
    color: "#007F3B",
  },
  {
    icon: PenTool,
    title: "CV Rewriter",
    desc: "Transform your CV into an achievement-focused, STAR-formatted document ready for NHS roles.",
    color: "#003087",
  },
];

const PLANS = [
  {
    name: "Free",
    price: "0",
    period: "",
    features: ["1 supporting statement/month", "2 ATS analyses/month", "Basic feedback"],
    cta: "Get Started",
    popular: false,
  },
  {
    name: "Pro",
    price: "19",
    period: "/month",
    features: [
      "10 Tailored NHS Supporting Information (SI) Generations",
      "JD & Person Specification Alignment",
      "Missing Skills Analysis",
      "Strength Summary & Targeted Recommendations",
      "Professional Structured Output",
      "Secure Document Processing",
    ],
    cta: "Upgrade to Pro",
    popular: true,
  },
  {
    name: "Premium",
    price: "39",
    period: "/month",
    features: [
      "Unlimited NHS Supporting Information (SI) Generations",
      "Unlimited Band-Calibrated Interview Simulations",
      "10 Detailed NHS-Standard Interview Questions per Simulation",
      "Structured Panel-Level Feedback on Every Response",
      "Clinical & Non-Clinical Evaluation Logic",
      "Leadership & Governance Calibration by Band",
      "JD & Person Specification Specific Question Mapping",
      "Secure Document Processing",
    ],
    cta: "Go Premium",
    popular: false,
  },
];

export default function LandingPage() {
  const { user } = useAuth();
  const buildVersion = process.env.REACT_APP_BUILD_VERSION;
  const shortBuild = buildVersion ? buildVersion.slice(0, 7) : "not-set";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[#003087] text-white hover:bg-[#002366]" data-testid="nav-dashboard-btn">Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#003087] font-medium" data-testid="nav-login-btn">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#003087] text-white hover:bg-[#002366]" data-testid="nav-register-btn">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F0F4F8] to-[#E6EFF8] py-20 md:py-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <Badge className="mb-4 bg-[#003087]/10 text-[#003087] border-none px-3 py-1">For NHS Professionals</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#003087] mb-6" style={{ fontFamily: "Public Sans" }}>
              Get <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#003087] to-[#00A3A6]">Shortlisted</span> for NHS Jobs with AI
            </h1>
            <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-xl">
              Generate NHS-ready supporting information, optimise your CV, and practise realistic panel interview questions — all aligned with NHS job descriptions and person specifications.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#003087] text-white hover:bg-[#002366] px-8 py-3 text-base shadow-md" data-testid="hero-get-started-btn">
                  Start Free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="border-[#003087] text-[#003087] hover:bg-[#F0F4F8] px-8 py-3 text-base" data-testid="hero-learn-more-btn">
                  See Features
                </Button>
              </a>
            </div>
            <p className="text-sm text-slate-500 mt-4" data-testid="hero-free-plan-info">
              No card required. Free plan includes: 1 Supporting Information generation, 2 Application analyses, 1 Interview simulation.
            </p>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-[#007F3B]" /> NHS Aligned</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#FFB81C]" /> Trusted by Clinicians</span>
            </div>
          </div>
          <div className="hidden lg:block animate-fade-in stagger-2">
            <img
              src="https://images.unsplash.com/photo-1758691462651-611d730c5272?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBjb25zdWx0YXRpb24lMjB0YWJsZXQlMjBsYXB0b3AlMjBvZmZpY2UlMjBjbGluaWNhbHxlbnwwfHx8fDE3NzA4NTczNjB8MA&ixlib=rb-4.1.0&q=85"
              alt="NHS Professional"
              className="rounded-xl shadow-lg w-full max-w-lg ml-auto object-cover"
              style={{ maxHeight: 420 }}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 max-w-7xl mx-auto px-6" data-testid="features-section">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#003087] mb-4" style={{ fontFamily: "Public Sans" }}>
            Four Tools. One Platform.
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to prepare, apply, and progress in your NHS career.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {FEATURES.map((f, i) => (
            <Card key={i} className="bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 rounded-xl" data-testid={`feature-card-${i}`}>
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "Public Sans" }}>{f.title}</h3>
                <p className="text-slate-600 leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Built for NHS Recruitment */}
      <section className="py-16 bg-[#F0F4F8]" data-testid="nhs-trust-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#003087] mb-4" style={{ fontFamily: "Public Sans" }}>
            Built for NHS Recruitment
          </h2>
          <p className="text-base text-slate-600 mb-8">BandBoost AI understands:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: FileText, label: "NHS Job Descriptions" },
              { icon: Target, label: "Person Specifications" },
              { icon: Check, label: "Shortlisting Criteria" },
              { icon: Shield, label: "Panel Interview Expectations" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                <item.icon className="w-5 h-5 text-[#003087]" />
                <span className="text-sm font-medium text-slate-700 text-center">{item.label}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-6" data-testid="credibility-line">
            Built by an NHS clinician and AI engineer to help healthcare professionals succeed in NHS recruitment.
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#003087] mb-4" style={{ fontFamily: "Public Sans" }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Start free and upgrade as you advance your career.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan, i) => (
              <Card
                key={i}
                className={`relative rounded-xl transition-all duration-300 ${plan.popular ? "border-2 border-[#003087] shadow-lg scale-[1.02]" : "border border-slate-200 shadow-sm"}`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#003087] text-white px-3">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "Public Sans" }}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-[#003087]">&pound;{plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feat, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-[#007F3B] mt-0.5 flex-shrink-0" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                  <Link to={user ? "/pricing" : "/register"}>
                    <Button
                      className={`w-full ${plan.popular ? "bg-[#003087] text-white hover:bg-[#002366]" : "bg-white text-[#003087] border border-[#003087] hover:bg-[#F0F4F8]"}`}
                      data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003087] text-white py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-6">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span className="font-bold" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-white/70">
              <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Use</Link>
              <span>&copy; {new Date().getFullYear()} BandBoost AI</span>
            </div>
          </div>
          <div className="border-t border-white/20 pt-4">
            <p className="text-xs text-white/50 text-center">
              This platform is an independent career support tool and is not affiliated with or endorsed by the NHS.
            </p>
            <p className="text-xs text-white/40 text-center mt-2">
              Built by <a href="https://botverseweb.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60 transition-colors">botverseweb.com</a>
            </p>
            <p className="text-xs text-white/40 text-center mt-1" data-testid="build-version-landing">
              Build: {shortBuild}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
